<template>
  <div class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <InfuseTabs>
      <InfuseRouteTab v-for="tab of tabs" :key="tab.name" :name="tab.name" :label="tab.label" :to="tab.to" />
    </InfuseTabs>

    <div class="h-full overflow-auto">
      <router-view :model="model" v-bind="$attrs" @reload-model="emit('reload-model')" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useFeatureStore } from '@infuse/cpms/shared/features/store';
import { useInfuseServices } from '@infuse/cpms/shared/features/store';
import { InfuseRouteTab, InfuseTabs } from '@infuse/ui/components';
import { useParentRoute } from '@infuse/ui/services';
import { computed, ComputedRef } from 'vue';
import { RouteRecordRaw } from 'vue-router';

interface Emits {
  (e: 'reload-model'): void;

  (e: 'submit'): void;
}

const emit = defineEmits<Emits>();

defineProps<{ model?: any }>();

const featureStore = useFeatureStore();
const parentRoute = useParentRoute();

const infuseServices = useInfuseServices();

const activeServiceTypes: ComputedRef<String[]> = computed(
  () => infuseServices?.data?.value?.map((infuseService) => infuseService.type) || []
);

const tabRoutes = computed(() => parentRoute.value?.children.filter((child) => child.meta?.type === 'tab') ?? []);

const tabs = computed(() => {
  const { VITE_DISABLED_CHARGING_STATION_TABS } = import.meta.env || '';
  let tabObjects: RouteRecordRaw[];

  // Filter away routes that require infuseServices, if they're not active.
  if (activeServiceTypes.value && activeServiceTypes.value.length > 0) {
    tabObjects = tabRoutes.value.filter((route) => {
      if (route.meta && route.meta.requiredInfuseService) {
        if (!activeServiceTypes.value.includes(route.meta.requiredInfuseService as string)) return;
      }
      // procceed normally otherwise.
      return route;
    });
  } else {
    tabObjects = tabRoutes.value;
  }

  // Filter away tabs per App .env vvariables
  if (VITE_DISABLED_CHARGING_STATION_TABS) {
    const disabledPaths = VITE_DISABLED_CHARGING_STATION_TABS.split(',').map((path: string) => path.trim());

    tabObjects = tabObjects.filter((route) => {
      if (route.path && disabledPaths) {
        if (disabledPaths.includes(route.path)) return;
      }

      return route;
    });
  }

  return tabObjects
    .filter((route) => !route.meta?.hidden)
    .filter((route) => !route.meta?.requiresFeature || featureStore.hasFeature(route.meta?.requiresFeature))
    .map((route) => ({
      name: route.name?.toString() ?? route.path,
      label: (route.meta?.label as string) ?? '',
      to: route.name ? { name: route.name } : route.path
    }));
});
</script>
